import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { PaymentActivityDetails } from '@paintscout/api'
import { getObjectLabels } from '@paintscout/util/builder'
import Highlight from '@paintscout/ui/components/Highlight'
import ActivityTitle from '../ActivityTitle'
import ActivityFooter from '../ActivityFooter'
import ActivityContent from '../ActivityContent'
import type { ActivityTypeComponentProps } from '../Activity'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'

import ActivityOwner from '../ActivityOwner'
import { useClientOptions } from '../../../providers/ClientOptionsProvider'
import ActivityDot from '../ActivityDot'
import ActivityContact from '../ActivityContact'

const useStyles = makeStyles<Theme, ActivityTypeComponentProps>((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& ul': {
      listStyle: 'none',
      margin: 0,
      padding: 0
    },
    '& li': {
      ...theme.typography.body1,
      textAlign: 'left',
      marginBottom: theme.spacing(0.25)
    },
    width: 'fit-content'
  },
  event: {
    color: theme.palette.text.secondary
  }
}))

export default function QuotePaymentActivity(props: ActivityTypeComponentProps) {
  const { options } = useClientOptions()
  const { quote } = getObjectLabels({ options })
  const { activity, showContent, showDocTypeDetails } = props
  const classes = useStyles(props)
  const { note, isProcessing, isFailed, deposit, invoice, provider } = activity.details as PaymentActivityDetails

  const isEstimatePayment = activity.details?.quote?.id && !invoice
  const final = invoice?.status === 'paid' || (activity.details?.events ?? []).some((event) => event === 'fully paid')

  const filteredEvents = (activity.details?.events || []).filter((event) => {
    return event !== 'fully paid' && !event.includes('still owing')
  })

  return (
    <>
      <ActivityTitle>
        <ActivityDot activity={activity} />
        {isFailed ? (
          <>
            <Highlight>Payment </Highlight>
            <Highlight>Failed</Highlight>
          </>
        ) : (
          <>
            <Highlight>{final ? 'Final ' : 'Partial '}</Highlight>
            <Highlight>Payment</Highlight>
            {deposit && ' (deposit)'} {isProcessing ? 'processing' : 'added'}
            {isEstimatePayment && showDocTypeDetails && (
              <span>
                {' '}
                to <Highlight>{quote.value}</Highlight>
              </span>
            )}
            {showDocTypeDetails && (
              <>{provider ? <ActivityContact activity={activity} /> : <ActivityOwner activity={activity} />}</>
            )}
          </>
        )}
      </ActivityTitle>
      {showContent && !!note && <ActivityContent className={classes.content}>{note}</ActivityContent>}
      {showContent && filteredEvents.length > 0 && (
        <ActivityContent className={classes.content}>
          <i>{filteredEvents.join(' · ')}</i>
        </ActivityContent>
      )}
      <QuoteActivitySubtitle activity={activity} showDocTypeDetails={showDocTypeDetails} />
      <ActivityFooter timestamp={activity.details.timestamp} />
    </>
  )
}
